import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { FormattedMessage } from '../../util/reactIntl';
import { generateImagePreview, revokeImagePreview } from '../../util/cloudinaryUpload';

import css from './FieldImageUpload.module.css';

const FieldImageUploadComponent = props => {
  const { input, label, accept, className, maxSize, required, meta } = props;
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  // Get validation error from React Final Form
  const hasValidationError = meta && meta.touched && meta.error;

  // Log validation state
  useEffect(() => {
    if (required) {
      console.log('🔒 [FieldImageUpload] Field is required');
      if (hasValidationError) {
        console.log('❌ [FieldImageUpload] Validation error:', meta.error);
      } else if (input.value) {
        console.log('✅ [FieldImageUpload] Validation passed - file present');
      }
    }
  }, [required, hasValidationError, input.value, meta]);

  // Clean up preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (preview) {
        revokeImagePreview(preview);
      }
    };
  }, [preview]);

  const handleFileChange = e => {
    const file = e.target.files[0];
    setError(null);

    console.log('📤 [FieldImageUpload] File input changed');

    if (!file) {
      console.log('ℹ️ [FieldImageUpload] No file selected');
      input.onChange(null);
      if (preview) {
        revokeImagePreview(preview);
      }
      setPreview(null);
      return;
    }

    console.log('📁 [FieldImageUpload] File selected:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)} KB`,
    });

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      console.error('❌ [FieldImageUpload] Invalid file type:', file.type);
      setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size
    const maxFileSize = maxSize || 5 * 1024 * 1024; // Default 5MB
    if (file.size > maxFileSize) {
      const sizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
      console.error('❌ [FieldImageUpload] File too large:', `${(file.size / 1024 / 1024).toFixed(2)} MB`);
      setError(`File size too large. Maximum size is ${sizeMB}MB`);
      return;
    }

    // Generate preview
    try {
      const previewUrl = generateImagePreview(file);

      // Revoke old preview if exists
      if (preview) {
        revokeImagePreview(preview);
      }

      console.log('✅ [FieldImageUpload] Preview generated successfully');
      console.log('📸 [FieldImageUpload] File stored in form state');

      setPreview(previewUrl);
      input.onChange(file);
    } catch (err) {
      console.error('❌ [FieldImageUpload] Preview generation error:', err);
      setError('Failed to load image preview');
    }
  };

  const handleRemove = () => {
    console.log('🗑️ [FieldImageUpload] Removing image...');
    input.onChange(null);
    if (preview) {
      revokeImagePreview(preview);
    }
    setPreview(null);
    setError(null);

    // Reset file input
    const fileInput = document.getElementById(input.name);
    if (fileInput) {
      fileInput.value = '';
    }
    console.log('✅ [FieldImageUpload] Image removed successfully');
  };

  return (
    <div className={classNames(css.root, className)}>
      {label && (
        <label className={css.label}>
          {label}
          {required && <span className={css.required}> *</span>}
        </label>
      )}

      <div className={css.inputWrapper}>
        <input
          id={input.name}
          name={input.name}
          type="file"
          accept={accept || 'image/jpeg,image/jpg,image/png,image/gif,image/webp'}
          onChange={handleFileChange}
          className={css.fileInput}
          required={required}
        />

        <label htmlFor={input.name} className={css.uploadButton}>
          <svg className={css.uploadIcon} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5.5 13.5L10 9l4.5 4.5M10 9v9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M16 13v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
          <span className={css.uploadButtonText}>
            {preview ? <FormattedMessage id="FieldImageUpload.change" /> : <FormattedMessage id="FieldImageUpload.upload" />}
          </span>
        </label>

        {preview && (
          <button
            type="button"
            onClick={handleRemove}
            className={css.removeButton}
            aria-label="Remove image"
          >
            <FormattedMessage id="FieldImageUpload.remove" />
          </button>
        )}
      </div>

      {preview && (
        <div className={css.previewWrapper}>
          <img src={preview} alt="Company logo preview" className={css.preview} />
        </div>
      )}

      {error && (
        <div className={css.error}>
          {error}
        </div>
      )}

      {hasValidationError && (
        <div className={css.validationError}>
          {meta.error}
        </div>
      )}

      <div className={css.helpText}>
        <FormattedMessage id="FieldImageUpload.helpText" />
        {required && <span> (Required)</span>}
      </div>
    </div>
  );
};

/**
 * Field component for image upload with preview
 * Integrates with React Final Form
 *
 * @param {Object} props
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {string} props.className - Additional CSS class
 * @param {string} props.accept - Accepted file types
 * @param {number} props.maxSize - Maximum file size in bytes
 * @param {boolean} props.required - Whether the field is required
 * @param {Function} props.validate - Validation function
 */
const FieldImageUpload = props => {
  const { validate } = props;
  return (
    <Field name={props.name} type="file" validate={validate}>
      {fieldProps => <FieldImageUploadComponent {...fieldProps} {...props} />}
    </Field>
  );
};

export default FieldImageUpload;
